// src/oura/components/docs/write/DocWritePanel.tsx
//
// The doc drawer's "Questions and sharing" fold (Docs phase 2, lane 3): one
// 1-line row per step Oura writes for a person, each with its state once,
// opening its own view in the sheet. A person does every step: Oura drafts,
// checks and counts.
//
//   Questions        drafts to send, or how many are in the doc
//   Name and lines   whether the name and Oura's 2 lines read in the format
//   Share            not shared yet, or since when
//   Review email     once shared with questions waiting: sent when, by whom
//   Chaser           only once the reply-by has passed
//   Client email     once one was sent: when, by whom (ourteam-backend#6697)
//
// Under the rows, "Email the client": on for a doc staff marked Done that the
// client can open, off with the reason otherwise. The drawer's foot holds two
// pills already (Open in Google Docs, Change the code), so it lives here.
//
// Rendered only for a doc `canWrite` allows (the write switch, a Google Doc,
// a client doc).

import type { BusinessDocuments, DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { MailIcon } from "lucide-react";
import { useWritesPaused } from "@context/maintenance";
import { DrawerFold, Dot, ListRow, PillButton, TYPE, type Tone } from "@oura/ui";
import { cn } from "../../../lib/cn";
import { docDate, plural } from "@oura/docs";
import { chaserDue, clientEmailBlocked, lastMessageOf, namesCurrent, reviewOpen, useDocMessages, type DocWriteView } from "./model";

const DRAFT_ON_OPEN = new Set(["review", "chaser", "client-email"]);

interface Row {
    key: DocWriteView;
    title: string;
    tone: Tone;
    meta?: string;
    late?: string;
    /** A row with nothing to open yet reads, and does not open. */
    open: boolean;
}

export function DocWritePanel({
    doc,
    detail,
    businessId,
    businessName,
    now,
    plan = null,
    onView,
}: {
    doc: StaffDocument;
    detail: DocumentDetail | null;
    businessId: string;
    businessName: string;
    now: number;
    /** The board's signed plan, for a monthly doc's "Month N" (ourteam-backend#6868). */
    plan?: BusinessDocuments["plan"];
    onView: (view: DocWriteView) => void;
}) {
    const messages = useDocMessages(businessId);
    const writesPaused = useWritesPaused();
    const list = detail?.questionList ?? [];
    const drafts = list.filter((q) => q.state === "draft").length;
    const sent = list.filter((q) => q.state === "sent").length;
    const check = detail?.questionCheck ?? null;
    const countsDiffer = Boolean(check && ((check.inList !== null && check.inList !== check.inOura) || (check.comments !== null && check.comments !== check.inOura)));
    const shared = doc.sharing.clientCanSee;
    const current = namesCurrent(doc, detail, businessName, now, plan);
    const review = lastMessageOf(messages.data, doc.id, "doc_review");
    const chaser = lastMessageOf(messages.data, doc.id, "doc_chaser");
    const delivery = lastMessageOf(messages.data, doc.id, "doc_delivery");
    const due = chaserDue(doc, now);
    // A chaser sent after the reply-by answers this one; an older one does not.
    const chasedSince = chaser && doc.replyBy ? chaser.sentAt.slice(0, 10) >= doc.replyBy : false;
    const sentLine = (m: { sentAt: string; sentBy: string }) => ["Sent", docDate(m.sentAt), "by", m.sentBy].filter(Boolean).join(" ");

    // Questions asked in the doc itself (no list in Oura) still count: never "None yet" beside them.
    const inDoc = (detail?.threads ?? []).filter((t) => t.kind === "question");
    const inDocApproved = inDoc.filter((t) => t.state === "approved").length;
    const inDocText =
        inDoc.length === 0 ? null : inDocApproved === inDoc.length ? `${plural(inDoc.length, "question")}, all approved` : `${plural(inDoc.length, "question")}, ${inDocApproved} approved`;

    const rows: Row[] = [
        {
            key: "questions",
            title: "Questions",
            tone: countsDiffer ? "amber" : drafts > 0 ? "blue" : sent > 0 || inDocText ? "green" : "gray",
            meta: countsDiffer ? "Counts differ" : drafts > 0 ? `${drafts} to send` : inDocText ?? (sent > 0 ? `${sent} in the doc` : "None yet"),
            open: true,
        },
    ];
    if (current !== null) {
        rows.push({ key: "lines", title: "Name and title lines", tone: current ? "green" : "blue", meta: current ? "In the format" : "Out of date", open: true });
    }
    rows.push({
        key: "share",
        title: "Share with the client",
        tone: shared ? "green" : "blue",
        meta: shared ? (docDate(doc.sharing.sharedAt) ? `Shared ${docDate(doc.sharing.sharedAt)}` : "Shared") : "Not shared yet",
        open: true,
    });
    if (shared) {
        const canReview = reviewOpen(doc);
        rows.push({
            key: "review",
            title: "Review email",
            tone: review ? "green" : canReview ? "blue" : "gray",
            meta: review ? sentLine(review) : canReview ? "Not sent" : "Nothing waiting",
            open: canReview,
        });
    }
    // The client email, once one went: when and by whom.
    if (delivery) {
        rows.push({ key: "client-email", title: "Client email", tone: "green", meta: sentLine(delivery), open: true });
    }
    const emailBlocked = clientEmailBlocked(doc);
    // Offered only once the reply-by has passed with questions still waiting.
    if (due) {
        rows.push({
            key: "chaser",
            title: "Chaser",
            tone: chasedSince ? "green" : "amber",
            meta: chasedSince && chaser ? sentLine(chaser) : undefined,
            late: chasedSince ? undefined : ["Reply-by passed", docDate(doc.replyBy)].filter(Boolean).join(" "),
            open: true,
        });
    }

    // Folded, the head keeps one fact: the next step a person has.
    const note = drafts > 0
        ? `${plural(drafts, "question")} to send`
        : !shared
            ? "Not shared yet"
            : reviewOpen(doc) && !review
                ? "Review email not sent"
                : due && !chasedSince
                    ? "Chaser due"
                    : countsDiffer
                        ? "Question counts differ"
                        : current === false
                            ? "Name out of date"
                            : `Shared ${docDate(doc.sharing.sharedAt) ?? ""}`.trim();

    return (
        <DrawerFold id="staff-doc.write" label="Questions and sharing" note={note} remember defaultOpen>
            <div>
                {/* The review email, chaser and client email views draft on open: while paused, their rows are not links. */}
                {rows.map((r) => (
                    <ListRow
                        key={r.key}
                        lead="dot"
                        icon={<Dot tone={r.tone} />}
                        title={r.title}
                        meta={r.meta}
                        late={r.late}
                        muted={!r.open && r.tone === "gray"}
                        onClick={r.open && !(writesPaused && DRAFT_ON_OPEN.has(r.key)) ? () => onView(r.key) : undefined}
                        stackMeta
                    />
                ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                <PillButton
                    size="sm"
                    writes
                    variant={!emailBlocked && !delivery ? "primary" : "secondary"}
                    icon={MailIcon}
                    disabled={Boolean(emailBlocked)}
                    aria-describedby={emailBlocked ? `client-email-why-${doc.id}` : undefined}
                    onClick={() => onView("client-email")}
                >
                    {delivery ? "Email the client again" : "Email the client"}
                </PillButton>
                {emailBlocked && (
                    <span id={`client-email-why-${doc.id}`} className={cn(TYPE.tc, "text-gray-500")}>
                        {emailBlocked}
                    </span>
                )}
            </div>
        </DrawerFold>
    );
}
